import { Component } from "react";
import type { ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { captureException } from "@/lib/posthog";

type ChatWorkspaceErrorBoundaryProps = {
    children: ReactNode;
};

type ChatWorkspaceErrorBoundaryState = {
    hasError: boolean;
};

export class ChatWorkspaceErrorBoundary extends Component<
    ChatWorkspaceErrorBoundaryProps,
    ChatWorkspaceErrorBoundaryState
> {
    state: ChatWorkspaceErrorBoundaryState = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: unknown) {
        console.error("ChatWorkspace crashed:", error);
        Sentry.captureException(error, { tags: { section: "ChatWorkspace" } });
        captureException(error, { section: "ChatWorkspace" });
    }

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="flex min-h-[60vh] w-full flex-col items-center justify-center gap-3 p-6">
                    <h2 className="text-lg font-semibold">Chat workspace hit an error.</h2>
                    <p className="text-sm text-muted-foreground text-center max-w-md">
                        Try reloading this view. If the problem persists, check the console for details.
                    </p>
                    <Button onClick={this.handleReload} data-testid="button-reload-chatworkspace">
                        Reload
                    </Button>
                </div>
            );
        }

        return this.props.children;
    }
}
